import React from 'react';
import { Box, Stack, Button, Chip, Divider, Typography, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { FC, useState } from 'react';
import {
  AwayMessageTimeInterval,
  DefaultCustomField,
  Message,
  MessageTemplate,
  MessageType,
} from 'src/constants/types';
import { convertHtmlToText } from 'src/helpers/helpers';
import { CampaignListItem, ScheduledType } from '../../types';
import { buildRecurringMessages } from '../../helpers/builders';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import { parseTime } from '../../helpers/helpers';
import { getAwayCustomMessageCampaign } from '../../slices';
import { useSelector } from 'react-redux';
import TemplateText from 'src/components/TemplateText/TemplateText';
import { MessageCreditEstimates } from 'src/components/MessageCreditEstimates';

interface RecurringMessagesFormProps {
  companyStatus: 'available' | 'away';
  awayMessageCampaign: CampaignListItem;
  customFields: DefaultCustomField[];
  messageTemplates: MessageTemplate[];
  submitSaveRecurringAwayMessages: (
    awayMessageCampaign: CampaignListItem,
    awayCustomMessageCampaign: CampaignListItem,
    messages: Message[],
    shouldActivateCampaign: boolean,
  ) => Promise<void>;
  setIsRecurringFormDirty: React.Dispatch<React.SetStateAction<boolean>>;
  shouldActivateCampaign: boolean;
}

export const RecurringMessagesForm: FC<RecurringMessagesFormProps> = props => {
  const {
    awayMessageCampaign,
    customFields,
    messageTemplates,
    submitSaveRecurringAwayMessages,
    setIsRecurringFormDirty,
    shouldActivateCampaign,
  } = props;
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down('md'));

  const [recurringMessages, setRecurringMessages] = useState<Message[]>(
    buildRecurringMessages(awayMessageCampaign),
  );

  const awayCustomMessageCampaign = useSelector(getAwayCustomMessageCampaign);

  const getOverlappedIntervals = (input: AwayMessageTimeInterval[]): AwayMessageTimeInterval[] => {
    if (input.length < 2) return [];

    const timeToMinutes = (date: Date): number => date.getHours() * 60 + date.getMinutes();

    const intervalSegments = input.map(interval => {
      const start = timeToMinutes(parseTime(interval.startTime ?? ''));
      const end = timeToMinutes(parseTime(interval.endTime ?? ''));
      return start < end
        ? [[start, end]]
        : [
            [start, 24 * 60],
            [0, end],
          ];
    });

    const overlappedIntervals: AwayMessageTimeInterval[] = [];

    for (let i = 0; i < input.length; i++) {
      for (let j = i + 1; j < input.length; j++) {
        const intervalA = input[i];
        const intervalB = input[j];

        const daysA = new Set(intervalA.daysOfWeek);
        const hasOverlappingDay = intervalB.daysOfWeek.some(day => daysA.has(day));
        if (!hasOverlappingDay) continue;

        const segmentsA = intervalSegments[i];
        const segmentsB = intervalSegments[j];

        for (const [aStart, aEnd] of segmentsA) {
          for (const [bStart, bEnd] of segmentsB) {
            if (aStart < bEnd && bStart < aEnd) {
              overlappedIntervals.push(input[i]);
              overlappedIntervals.push(input[j]);
            }
          }
        }
      }
    }
    return overlappedIntervals;
  };

  const getScheduledInterval = (message: Message): AwayMessageTimeInterval => {
    return message.scheduledIntervals && message.scheduledIntervals.length
      ? message.scheduledIntervals[0]
      : ({
          daysOfWeek: [],
          startTime: '',
          endTime: '',
        } as AwayMessageTimeInterval);
  };

  const handleStartTimeChange = (message: Message, time: string, index: number) => {
    const interval = getScheduledInterval(message);

    const newInterval = { ...interval };
    newInterval.startTime = time;
    newInterval.endTime = '';
    const messages = recurringMessages.map((m, i) => ({
      ...m,
      scheduledIntervals: i === index ? [newInterval] : m.scheduledIntervals,
    }));

    setIsRecurringFormDirty(true);
    setRecurringMessages(messages);
  };

  const handleEndTimeChange = (message: Message, time: string, index: number) => {
    const interval = getScheduledInterval(message);
    const newInterval = { ...interval };
    newInterval.endTime = time;

    const messages = recurringMessages.map((m, i) => ({
      ...m,
      scheduledIntervals: i === index ? [newInterval] : m.scheduledIntervals,
    }));
    setIsRecurringFormDirty(true);
    setRecurringMessages(messages);
  };

  const convertTimeStringToDateTime = (time: string) => {
    if (time) {
      return new Date('1970-01-01T' + time).toUTCString();
    }
    return '';
  };

  const renderTimePickers = (message: Message, index: number) => {
    const time =
      message.scheduledIntervals && message.scheduledIntervals.length
        ? message.scheduledIntervals[0]
        : ({
            daysOfWeek: [],
            startTime: '',
            endTime: '',
          } as AwayMessageTimeInterval);

    const hideAmClass =
      dayjs(convertTimeStringToDateTime(time.startTime)).hour() > 12
        ? {
            '& .MuiMenuItem-gutters[aria-label="AM"]': {
              display: 'none',
            },
          }
        : null;

    return (
      <Box display="flex" gap={isMobile ? "5px" : "5px"} flexDirection="row">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            slotProps={{ textField: { error: false } }}
            sx={{
              '& .MuiStack-root': { paddingTop: 0 },
              '&.MuiTextField-root': {
                minWidth: isMobile ? '90px' : '100px',
              },
              '& .MuiOutlinedInput-root': {
                height: '30px',
                width: isMobile ? '110px' : '140px',
                marginRight: 0,
                marginLeft: isMobile ? '0px' : '10px',
              },
              overflow: 'hidden',
            }}
            ampm
            format="hh:mm A"
            closeOnSelect={false}
            onChange={value => handleStartTimeChange(message, value?.format('HH:mm') || '', index)}
            value={dayjs(convertTimeStringToDateTime(time.startTime))}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            slotProps={{
              textField: { error: false },
              popper: {
                sx: hideAmClass,
              },
            }}
            sx={{
              '& .MuiStack-root': { paddingTop: 0 },
              '& .MuiOutlinedInput-root': {
                height: '30px',
                width: isMobile ? '110px' : '140px',
                marginRight: 0,
              },

              '& .MuiMenuItem-root': {
                display: 'none',
              },
              overflow: 'hidden',
            }}
            ampm
            format="hh:mm A"
            closeOnSelect={false}
            minTime={dayjs(convertTimeStringToDateTime(time.startTime))}
            onOpen={() => {
              const startTimeHour = dayjs(convertTimeStringToDateTime(time.startTime)).hour();
              if (time.endTime === '' && startTimeHour > 12) {
                handleEndTimeChange(message, `${startTimeHour}:00`, index);
              }
            }}
            onChange={value => handleEndTimeChange(message, value?.format('HH:mm') || '', index)}
            value={dayjs(convertTimeStringToDateTime(time.endTime))}
          />
        </LocalizationProvider>
      </Box>
    );
  };

  const renderDayChips = (message: Message, messageIndex: number) => {
    const days = [
      { label: 'M', value: 1 },
      { label: 'T', value: 2 },
      { label: 'W', value: 3 },
      { label: 'T', value: 4 },
      { label: 'F', value: 5 },
      { label: 'S', value: 6 },
      { label: 'S', value: 0 },
    ];
    return days.map(day => {
      const isDaySelected =
        message.scheduledIntervals && message.scheduledIntervals[0]?.daysOfWeek.includes(day.value);
      return (
        <Chip
          key={day.label}
          style={{
            fontSize: isMobile ? '1rem' : '1rem',
            height: isMobile ? '36px' : '32px',
            width: isMobile ? '36px' : '32px',
            borderRadius: '50%',
          }}
          onClick={() => {
            const interval = getScheduledInterval(message);
            if (!interval) {
              return;
            }
            const newInterval = { ...interval };
            newInterval.daysOfWeek = interval.daysOfWeek.includes(day.value)
              ? interval.daysOfWeek.filter(d => d !== day.value)
              : [...interval.daysOfWeek, day.value];

            message.scheduledIntervals = [newInterval];
            const messages = recurringMessages.map((m, i) => ({
              ...m,
              scheduledIntervals: messageIndex === i ? [newInterval] : m.scheduledIntervals,
            }));
            setIsRecurringFormDirty(true);
            setRecurringMessages(messages);
          }}
          color={isDaySelected ? 'primary' : 'default'}
          label={day.label}
          size="small"
          variant={isDaySelected ? 'filled' : 'outlined'}
        />
      );
    });
  };

  const addNewMessage = () => {
    const newMessage = {
      text: '',
      type: 'sms' as MessageType,
      subject: '',
      links: [],
      position: recurringMessages.length + 1,
      scheduledType: 'time_intervals' as ScheduledType,
      scheduledIntervals: [
        {
          startTime: '',
          endTime: '',
          daysOfWeek: [],
        },
      ],
    } as unknown as Message;
    setIsRecurringFormDirty(true);
    setRecurringMessages([...recurringMessages, newMessage]);
  };

  const hasInvalidMessages = () => {
    const invalidMessages = recurringMessages.find(
      m =>
        !m.text ||
        m.scheduledIntervals?.length === 0 ||
        (m.scheduledIntervals &&
          m.scheduledIntervals.length &&
          (m.scheduledIntervals[0].daysOfWeek.length === 0 ||
            m.scheduledIntervals[0].startTime === '' ||
            m.scheduledIntervals[0].endTime === '')),
    );
    return !!invalidMessages;
  };

  const isOverlappedMessage = (
    message: Message,
    overlappedIntervals: AwayMessageTimeInterval[],
  ) => {
    return (
      message.scheduledIntervals &&
      message.scheduledIntervals.length &&
      overlappedIntervals.includes(message.scheduledIntervals[0])
    );
  };

  const overlappedIntervals = getOverlappedIntervals(
    recurringMessages.flatMap(m => m.scheduledIntervals || []),
  );

  const formSnapshot = JSON.stringify(buildRecurringMessages(awayMessageCampaign));

  return (
    <Box sx={{ position: 'relative', marginTop: '10px' }}>
      {recurringMessages.map((m, index) => (
        <Box key={m.position} sx={{ marginTop: '15px', position: 'relative' }}>
          <Box
            display="flex"
            alignItems="center"
            marginBottom="10px"
            justifyContent="space-between"
            flexDirection={isMobile ? "column" : "row"}
            gap={isMobile ? "10px" : "0px"}
          >
            {isMobile ? (
              <>
                <Box display="flex" gap="2px" justifyContent="space-around" width="100%">
                  {renderDayChips(m, index)}
                </Box>
                <Box display="flex" gap="5px" alignItems="center" justifyContent="space-between" width="100%">
                  {renderTimePickers(m, index)}
                  <Button
                    sx={{ minWidth: '10px' }}
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      const messages = recurringMessages.filter((_, i) => index !== i);
                      setIsRecurringFormDirty(true);
                      setRecurringMessages(messages);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Box display="flex" gap="3px">
                  {renderDayChips(m, index)}
                  {renderTimePickers(m, index)}
                </Box>
                <Button
                  sx={{ minWidth: '10px' }}
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    const messages = recurringMessages.filter((_, i) => index !== i);
                    setIsRecurringFormDirty(true);
                    setRecurringMessages(messages);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </Button>
              </>
            )}
          </Box>

          <TemplateText
            showConfirmationText={false}
            confirmationText=""
            clearText={() => {
              const messages = recurringMessages.map((message, i) => {
                return { ...message, text: index === i ? '' : message.text };
              });
              setIsRecurringFormDirty(true);
              setRecurringMessages(messages);
            }}
            setFocus={false}
            customFields={customFields}
            emodjiPickerPosition={{ top: '0', right: '0' }}
            messageTemplates={messageTemplates}
            text={m.text}
            updateText={newText => {
              const messages = recurringMessages.map((message, i) => {
                return {
                  ...message,
                  text: i === index ? convertHtmlToText(newText) : message.text,
                };
              });
              setIsRecurringFormDirty(m.text !== convertHtmlToText(newText));
              setRecurringMessages(messages);
            }}
            note={
              <MessageCreditEstimates
                viewType="campaign"
                messageType="sms"
                messageText={m.text}
                hasAttachments={false}
                sx={{ py: 0.625, px: 1.25 }}
              />
            }
          />
          <Typography color="error">
            {isOverlappedMessage(m, overlappedIntervals)
              ? ' This message has overlapped schedule with another message.'
              : ''}
          </Typography>
          {index !== recurringMessages.length - 1 && (
            <Divider sx={{ marginTop: '10px', marginBottom: '25px' }} />
          )}
        </Box>
      ))}
      {!hasInvalidMessages() && (
        <Stack direction="row">
          <Button
            variant="outlined"
            style={{ width: '100%', marginTop: '20px' }}
            onClick={addNewMessage}
          >
            Add Message
          </Button>
        </Stack>
      )}

      <Stack direction="row">
        <Button
          disabled={
            hasInvalidMessages() ||
            overlappedIntervals.length > 0 ||
            formSnapshot === JSON.stringify(recurringMessages)
          }
          variant="contained"
          style={{ width: '100%', marginTop: '20px' }}
          onClick={() =>
            submitSaveRecurringAwayMessages(
              awayMessageCampaign,
              awayCustomMessageCampaign!,
              recurringMessages,
              shouldActivateCampaign,
            )
          }
        >
          {awayMessageCampaign.id && awayMessageCampaign.scheduledMessages.length
            ? 'Save'
            : 'Create'}
        </Button>
      </Stack>
    </Box>
  );
};
